'use client';

import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { AddIcon, SubtractIcon } from '@/components/ui/Icons';

interface QuantitySelectorProps {
  initialQuantity?: number;
  maxQuantity?: number;
  onQuantityChange?: (quantity: number) => void;
  className?: string;
  quantityLimitLabel?: string;
}

const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  initialQuantity = 1,
  maxQuantity,
  onQuantityChange,
  className = '',
  quantityLimitLabel,
}) => {
  const [quantity, setQuantity] = useState(initialQuantity);

  // Sync internal state with prop changes (e.g., when SWR rolls back optimistic updates)
  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity]);

  const isAtMaxQuantity = maxQuantity !== undefined && quantity >= maxQuantity;

  const handleDecrease = () => {
    const newQuantity = Math.max(0, quantity - 1);
    setQuantity(newQuantity);

    onQuantityChange?.(newQuantity);
  };

  const handleIncrease = () => {
    if (isAtMaxQuantity) return;

    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    onQuantityChange?.(newQuantity);
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div
        className={classNames(
          'flex items-center rounded-md px-[14.5px] border text-grey-dark h-8',
          isAtMaxQuantity ? 'border-warning' : 'border-grey-mid-light',
        )}
        data-testid="quantity-selector"
        role="group"
        aria-label="Quantity selector"
      >
        <button
          className="flex items-center justify-center hover:text-gray-900 transition-colors cursor-pointer"
          aria-label="Decrease quantity"
          type="button"
          onClick={handleDecrease}
          data-testid="quantity-decrease-button"
        >
          <SubtractIcon height="10" width="10" />
        </button>
        <span
          className="text-sm font-bold px-4 leading-[20px]"
          data-testid="quantity-display"
          aria-live="polite"
          aria-label={`Current quantity: ${quantity}`}
        >
          {quantity}
        </span>
        <button
          className={classNames(
            'flex items-center justify-center transition-colors',
            isAtMaxQuantity
              ? 'text-grey-mid-light cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer',
          )}
          aria-label="Increase quantity"
          aria-disabled={isAtMaxQuantity}
          type="button"
          onClick={handleIncrease}
          disabled={isAtMaxQuantity}
          data-testid="quantity-increase-button"
        >
          <AddIcon height="10" width="10" />
        </button>
      </div>
      {isAtMaxQuantity && (
        <span
          className="text-xs text-warning font-bold"
          data-testid="quantity-limit-label"
        >
          {quantityLimitLabel ?? 'Qty limit:'} {maxQuantity}
        </span>
      )}
    </div>
  );
};

export default QuantitySelector;
